import '../App.css';

import {
    CheckCircledIcon,
    CrossCircledIcon,
    FontBoldIcon,
    FontItalicIcon,
    Link2Icon,
    ListBulletIcon,
    UnderlineIcon,
    UpdateIcon,
} from '@radix-ui/react-icons';
import { Button } from '../components/ui/button';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../components/ui/form';

import { useCallback, useEffect, useState } from 'react';
import {
    createButton,
    createDropdown,
    Editor,
    EditorProvider,
    Toolbar,
} from 'react-simple-wysiwyg';

import axios from 'axios';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from '../components/ui/use-toast';
import api from '../lib/api';
import DOMPurify from 'dompurify';

export const BtnBulletList = createButton(
    'Bullet list',
    <ListBulletIcon />,
    'insertUnorderedList',
);
export const BtnItalic = createButton('Italic', <FontItalicIcon />, 'italic');
export const BtnBold = createButton('Bold', <FontBoldIcon />, 'bold')
export const BtnUnderline = createButton('Underline', <UnderlineIcon />, 'underline')
export const BtnLink = createButton('Link', <Link2Icon />, ({ $selection }) => {
    if ($selection?.nodeName === 'A') {
        document.execCommand('unlink');
    } else {
        // eslint-disable-next-line no-alert
        document.execCommand('createLink', false, prompt('URL', '') || undefined);
    }
});
export const BtnStyles = createDropdown('Stilar', [
    ['Normal', 'formatBlock', 'DIV'],
    ['𝐑𝐮𝐛𝐫𝐢𝐤 𝟏', 'formatBlock', 'H1'],
    ['Rubrik 2', 'formatBlock', 'H2'],
]);

function ClubInformation() {
    const form = useForm()
    const navigate = useNavigate();
    const [club, setClub] = useState([]);
    const { clubId } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [description, setDescription] = useState('');
    const [payment_information, setPaymentInformation] = useState('');


    const fetchClubInformation = useCallback(() => {

        setIsLoading(true); // Start loading data

        axios.get(`https://skhrf-api.cmdigital.se/v1/clubs?clubId=${clubId}`)
            .then(response => {
                const clubData = response.data[0];
                setClub(clubData);
                const sanitizedDescription = DOMPurify.sanitize(clubData.description, {
                    ALLOWED_TAGS: ['div', 'br', 'b', 'i', 'u', 'a', 'h1', 'h2'],
                    ALLOWED_ATTR: ['href']
                });
                const sanitizedPaymentInfo = DOMPurify.sanitize(clubData.payment_information, {
                    ALLOWED_TAGS: ['div', 'br', 'b', 'i', 'u', 'a', 'h1', 'h2'],
                    ALLOWED_ATTR: ['href']
                });
                setDescription(sanitizedDescription || ''); // Ensure default empty string
                setPaymentInformation(sanitizedPaymentInfo || ''); // Ensure default empty string
                setIsLoading(false); // Data fetched, set loading to false
            })
            .catch(error => {
                console.error('Error fetching clubs:', error);
                setIsLoading(false); // Error occurred, set loading to false
            });
    }, [clubId]);

    const sendClubInformation = (e) => {
        if (isLoading) return;
        e.preventDefault();
        setIsLoading(true);

        // Sanitize the HTML from the editor before sending
        const sanitizedDescription = DOMPurify.sanitize(description, {
            ALLOWED_TAGS: ['div', 'br', 'b', 'i', 'u', 'a', 'h1', 'h2'],
            ALLOWED_ATTR: ['href']
        });
        const sanitizedPaymentInfo = DOMPurify.sanitize(payment_information, {
            ALLOWED_TAGS: ['div', 'br', 'b', 'i', 'u', 'a', 'h1', 'h2', 'ul', 'li'],
            ALLOWED_ATTR: ['href']
        });

        api.post(`https://skhrf-api.cmdigital.se/v1/clubs/profile`, {
            targetClubId: parseInt(clubId),
            description: sanitizedDescription,
            payment_information: sanitizedPaymentInfo,
        }).then(response => {
            toast({
                variant: 'success',
                title: 'Informationen uppdaterades!',
                description: 'Informationen uppdaterades utan problem.'
            })

            setIsLoading(false);
            fetchClubInformation();
        }).catch(error => {
            console.error('Error updating club information:', error);
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error?.response?.data?.error[0],
            });
            setIsLoading(false);
        });
    };

    useEffect(() => {
        fetchClubInformation();
    }, [fetchClubInformation]);

    return (
        <div className='flex w-full items-center justify-center'>
            <div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'>
                <h1 className='font-medium text-2xl'>Ändra information om
                    {isLoading ? (<Skeleton className='h-10 w-52' />) : (
                        ' ' + club.name
                    )}</h1>
                <Form {...form} >
                    <form /*onSubmit={form.handleSubmit(onSubmit)}*/ className="flex flex-col gap-6 w-full sm:w-1/2">
                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>Föreningsinformation</FormLabel>
                                    <FormControl>
                                        <EditorProvider>
                                            <Editor disabled={isLoading} value={description} onChange={(e) => setDescription(e.target.value)}>
                                                <Toolbar>
                                                    <BtnBold />
                                                    <BtnItalic />
                                                    <BtnUnderline />
                                                    <BtnBulletList />
                                                    <BtnLink />
                                                    <BtnStyles />
                                                </Toolbar>
                                            </Editor>
                                        </EditorProvider>
                                    </FormControl>
                                    <FormDescription>
                                        Här kan du ändra den information som visas om föreningen.
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="payment_information"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>Betalningsinformation</FormLabel>
                                    <FormControl>
                                        <EditorProvider>
                                            <Editor disabled={isLoading} value={payment_information} onChange={(e) => setPaymentInformation(e.target.value)}>
                                                <Toolbar>
                                                    <BtnBold />
                                                    <BtnItalic />
                                                    <BtnUnderline />
                                                    <BtnBulletList />
                                                    <BtnLink />
                                                    <BtnStyles />
                                                </Toolbar>
                                            </Editor>
                                        </EditorProvider>
                                    </FormControl>
                                    <FormDescription>
                                        Här ändrar du föreningens betalningsinformation.
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                            <Button type='button' variant='secondary' onClick={() => navigate(-1)}>
                                <CrossCircledIcon className='mr-3' />
                                Avbryt
                            </Button>
                            <Button type="submit" disabled={isLoading} onClick={sendClubInformation}>
                                {isLoading ? <UpdateIcon className='loader-spinner' /> : <><CheckCircledIcon className='mr-3' />
                                    Spara ändringar</>}
                            </Button>
                        </div>
                    </form>
                </Form>
            </div>
        </div>
    );
}

export default ClubInformation;